// src/pages/AssignmentsMatrixPage.js
//
// Ticket 08 full UI: admin assignment matrix. Lets an orgAdmin
// choose which leaf questions each reviewer sees on each paper for
// the active study. Writes land in the assignments collection with
// the ticket-07 deterministic doc id
// `${studyId}_${reviewerId}_${paperId}`.
//
// Layout:
//   Table with papers as rows, reviewers as columns. Each cell is a
//   button showing "N/M" (assigned questions / total leaf questions)
//   or "Assign" when there's no doc yet. Clicking a cell opens a modal
//   with a checkbox list of every leaf question in the questionnaire
//   plus "all" / "none" shortcuts. Save upserts the assignment;
//   clearing all checkboxes removes the assignment entirely.
//
// Read path uses loadAssignmentsForStudy (one list) so the grid
// renders from a single Firestore read; writes happen per-cell.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  Grid,
  Save,
  X,
  Loader,
  AlertTriangle,
  CheckCircle,
  Trash2,
} from 'lucide-react';
import { useSession } from '../auth/sessionContext';
import { useActiveStudy } from '../hooks/useActiveStudy';
import { loadAllPapersForStudy } from '../data/papersHelpers';
import { listMembershipsForOrg } from '../auth/membershipHelpers';
import {
  loadAssignmentsForStudy,
  upsertAssignment,
  removeAssignment,
} from '../data/assignmentsHelpers';
import {
  loadQuestionnaire,
  getAllLeafQuestions,
} from '../data/questionnaireHelpers';

const AssignmentsMatrixPage = () => {
  const { activeOrg, user } = useSession();
  const { studyId, studyName, loading: studyLoading } = useActiveStudy();
  const orgId = activeOrg?.orgId || null;

  const [papers, setPapers] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [leafQuestions, setLeafQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ type: 'idle', message: '' });
  const [editing, setEditing] = useState(null); // { paperId, reviewerId, questionIds: Set }

  const load = useCallback(async () => {
    if (!orgId || !studyId) return;
    setLoading(true);
    try {
      const [p, m, a, q] = await Promise.all([
        loadAllPapersForStudy(studyId),
        listMembershipsForOrg(orgId),
        loadAssignmentsForStudy(studyId),
        loadQuestionnaire(studyId),
      ]);
      setPapers((p || []).filter((pp) => !pp.archived));
      setReviewers((m || []).filter((mm) => mm.role === 'reviewer'));
      setAssignments(a || []);
      setLeafQuestions(q ? getAllLeafQuestions(q) : []);
    } catch (err) {
      setStatus({ type: 'error', message: `Load failed: ${err.message}` });
    } finally {
      setLoading(false);
    }
  }, [orgId, studyId]);

  useEffect(() => {
    load();
  }, [load]);

  // (paperId, reviewerUid) -> assignment doc
  const byKey = useMemo(() => {
    const m = {};
    for (const a of assignments) {
      m[`${a.paperId}::${a.reviewerId}`] = a;
    }
    return m;
  }, [assignments]);

  const openCellEditor = (paperId, reviewerUid) => {
    const existing = byKey[`${paperId}::${reviewerUid}`];
    setEditing({
      paperId,
      reviewerId: reviewerUid,
      questionIds: new Set(existing?.questionIds || []),
    });
  };

  const closeCellEditor = () => setEditing(null);

  const toggleQuestion = (qid) => {
    setEditing((prev) => {
      if (!prev) return prev;
      const next = new Set(prev.questionIds);
      if (next.has(qid)) next.delete(qid);
      else next.add(qid);
      return { ...prev, questionIds: next };
    });
  };
  const selectAll = () => {
    setEditing((prev) =>
      prev ? { ...prev, questionIds: new Set(leafQuestions.map((q) => q.id)) } : prev,
    );
  };
  const selectNone = () => {
    setEditing((prev) => (prev ? { ...prev, questionIds: new Set() } : prev));
  };

  const saveCell = async () => {
    if (!editing) return;
    const { paperId, reviewerId, questionIds } = editing;
    setStatus({ type: 'loading', message: 'Saving assignment...' });
    try {
      if (questionIds.size === 0) {
        await removeAssignment({ studyId, reviewerId, paperId });
        setStatus({ type: 'success', message: 'Assignment cleared.' });
      } else {
        await upsertAssignment({
          studyId,
          reviewerId,
          paperId,
          orgId,
          questionIds: Array.from(questionIds),
          createdBy: user?.uid || 'admin',
        });
        setStatus({
          type: 'success',
          message: `Saved assignment (${questionIds.size} question${questionIds.size === 1 ? '' : 's'}).`,
        });
      }
      setEditing(null);
      await load();
    } catch (err) {
      setStatus({ type: 'error', message: `Save failed: ${err.message}` });
    }
  };

  const clearCell = async () => {
    if (!editing) return;
    setStatus({ type: 'loading', message: 'Removing assignment...' });
    try {
      await removeAssignment({
        studyId,
        reviewerId: editing.reviewerId,
        paperId: editing.paperId,
      });
      setStatus({ type: 'success', message: 'Assignment removed.' });
      setEditing(null);
      await load();
    } catch (err) {
      setStatus({ type: 'error', message: `Remove failed: ${err.message}` });
    }
  };

  const statusStyles = {
    idle: 'bg-slate-100 text-slate-700',
    info: 'bg-blue-100 text-blue-800',
    success: 'bg-green-100 text-green-800',
    error: 'bg-red-100 text-red-800',
    loading: 'bg-yellow-100 text-yellow-800',
  };

  if (studyLoading || !activeOrg || !studyId) {
    return (
      <main className="p-8">
        <div className="text-slate-500">Pick an org and a study first.</div>
      </main>
    );
  }

  const totalQuestions = leafQuestions.length;

  return (
    <main className="p-4 lg:p-8 pb-32">
      <div className="mb-6">
        <Link
          to="/admin/dashboard"
          className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-800"
        >
          <ChevronLeft size={18} />
          Back to Progress Overview
        </Link>
      </div>
      <div className="bg-white p-6 rounded-xl border border-slate-200 academic-shadow-lg">
        <h2 className="text-2xl font-bold text-slate-900 mb-1 flex items-center gap-2">
          <Grid size={22} /> Assignment matrix
        </h2>
        <p className="text-sm text-slate-600 mb-4">
          Choose which questions each reviewer sees on each paper in{' '}
          <strong>{studyName || studyId}</strong>.{' '}
          {totalQuestions > 0 && (
            <>
              The questionnaire has <strong>{totalQuestions}</strong> leaf question
              {totalQuestions === 1 ? '' : 's'}.
            </>
          )}
        </p>

        {status.message && (
          <div className={`p-3 rounded-lg mb-4 text-sm flex items-center gap-3 ${statusStyles[status.type]}`}>
            {status.type === 'error' && <AlertTriangle size={16} />}
            {status.type === 'success' && <CheckCircle size={16} />}
            {status.type === 'loading' && <Loader size={16} className="animate-spin" />}
            <span>{status.message}</span>
            <button
              onClick={() => setStatus({ type: 'idle', message: '' })}
              className="ml-auto opacity-70"
              aria-label="Dismiss status"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {loading ? (
          <div className="py-8 text-slate-500 flex items-center gap-3">
            <Loader size={20} className="animate-spin" /> Loading matrix...
          </div>
        ) : totalQuestions === 0 ? (
          <div className="py-6 text-sm text-slate-600">
            <p className="mb-3">
              The questionnaire for this study has no leaf questions yet. Add
              questions in the <strong>Questionnaire editor</strong> before
              assigning them.
            </p>
            <Link
              to="/admin/questionnaire"
              className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700"
            >
              Open Questionnaire Editor
            </Link>
          </div>
        ) : papers.length === 0 ? (
          <div className="py-6 text-sm text-slate-600">No papers in this study yet.</div>
        ) : reviewers.length === 0 ? (
          <div className="py-6 text-sm text-slate-600">
            No reviewers in this org yet. Add them via <strong>Members</strong>.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-sm" data-testid="assignment-matrix">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-slate-100 border border-slate-200 p-2 text-left font-semibold text-slate-700 min-w-[200px]">
                    Paper
                  </th>
                  {reviewers.map((r) => (
                    <th
                      key={r.uid}
                      className="border border-slate-200 p-2 text-left font-semibold text-slate-700 min-w-[140px]"
                    >
                      <div className="truncate" title={r.email}>
                        {r.displayName || r.email}
                      </div>
                      <div className="text-xs font-normal text-slate-500 truncate">
                        {r.email}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {papers.map((p) => (
                  <tr key={p.id}>
                    <td className="sticky left-0 bg-white border border-slate-200 p-2 font-medium text-slate-800 max-w-xs">
                      <div className="truncate" title={p.title}>{p.title}</div>
                    </td>
                    {reviewers.map((r) => {
                      const a = byKey[`${p.id}::${r.uid}`];
                      const n = a?.questionIds?.length || 0;
                      return (
                        <td
                          key={r.uid}
                          className="border border-slate-200 p-1"
                          data-paper-id={p.id}
                          data-reviewer-id={r.uid}
                        >
                          <button
                            onClick={() => openCellEditor(p.id, r.uid)}
                            className={`w-full px-2 py-1.5 rounded text-xs font-semibold ${
                              n > 0
                                ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                                : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                            }`}
                          >
                            {n > 0 ? `${n}/${totalQuestions}` : 'Assign'}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <CellEditorModal
          editing={editing}
          leafQuestions={leafQuestions}
          papers={papers}
          reviewers={reviewers}
          onToggle={toggleQuestion}
          onSelectAll={selectAll}
          onSelectNone={selectNone}
          onSave={saveCell}
          onClear={clearCell}
          onClose={closeCellEditor}
        />
      )}
    </main>
  );
};

const CellEditorModal = ({
  editing,
  leafQuestions,
  papers,
  reviewers,
  onToggle,
  onSelectAll,
  onSelectNone,
  onSave,
  onClear,
  onClose,
}) => {
  const paper = papers.find((p) => p.id === editing.paperId);
  const reviewer = reviewers.find((r) => r.uid === editing.reviewerId);
  return (
    <div
      className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-xl academic-shadow-lg max-w-xl w-full max-h-[80vh] flex flex-col"
        data-testid="assignment-modal"
      >
        <div className="p-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-slate-900 truncate">
              {paper?.title || editing.paperId}
            </h3>
            <p className="text-sm text-slate-600 truncate">
              Assignment for <strong>{reviewer?.email || editing.reviewerId}</strong>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-slate-100"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-4 flex gap-2 border-b border-slate-200 text-xs">
          <button
            onClick={onSelectAll}
            className="px-3 py-1 bg-slate-100 hover:bg-slate-200 rounded font-semibold"
          >
            Select all
          </button>
          <button
            onClick={onSelectNone}
            className="px-3 py-1 bg-slate-100 hover:bg-slate-200 rounded font-semibold"
          >
            Clear
          </button>
          <span className="ml-auto text-slate-500 self-center">
            {editing.questionIds.size} / {leafQuestions.length} selected
          </span>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-1.5">
          {leafQuestions.map((q) => (
            <label
              key={q.id}
              className="flex items-start gap-3 p-2 rounded hover:bg-slate-50 cursor-pointer"
              data-question-id={q.id}
            >
              <input
                type="checkbox"
                checked={editing.questionIds.has(q.id)}
                onChange={() => onToggle(q.id)}
                className="mt-1"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-800">{q.text || q.id}</div>
                <div className="text-xs text-slate-500 font-mono">
                  {q.id} · {q.type}
                </div>
              </div>
            </label>
          ))}
        </div>
        <div className="p-4 border-t border-slate-200 flex gap-2 justify-end">
          <button
            onClick={onClear}
            className="px-3 py-2 bg-white border border-red-200 text-red-700 rounded-lg text-sm font-semibold hover:bg-red-50 flex items-center gap-2"
          >
            <Trash2 size={14} /> Remove
          </button>
          <button
            onClick={onClose}
            className="px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm font-semibold hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 flex items-center gap-2"
          >
            <Save size={14} /> Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignmentsMatrixPage;
